/**
 * =============================================================================
 * IDS CRM Web - Accommodation Directory
 * =============================================================================
 *
 * One vetted list of overnight stays near sites (Gareth 2026-08-07). Whoever
 * books — ops coordinators, engineers sorting themselves a hotel on the road,
 * team leaders/managers as backup — sees the same ratings and do-not-book
 * warnings. Unsuitable places stay VISIBLE (struck through, with the reason)
 * so the warning does its job; they can be hidden with the filter.
 *
 * Everyone can read; commercial and financial are read-only.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import CoordsCopy from '../components/CoordsCopy';
import CountrySelect from '../components/CountrySelect';
import UseMyLocationButton from '../components/UseMyLocationButton';
import { validateCoordinatePair } from '../utils/coordinates';
import { formatDateKey, toDateKey } from '../utils/dates';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Assets.css';
import './Accommodation.css';

const BOOKING_ROLES = ['engineering', 'engineer', 'senior_engineer', 'desk_engineer', 'team_leader', 'operations_coordinator', 'manager', 'admin'];

export const ACCOMMODATION_TYPES = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'bnb', label: 'B&B' },
  { value: 'pub', label: 'Pub rooms' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'other', label: 'Other' }
];

const typeLabel = (value) => ACCOMMODATION_TYPES.find(t => t.value === value)?.label || value;

export function RatingChip({ avgRating, stayCount }) {
  if (!avgRating) return <span className="accom-rating none">No stays yet</span>;
  const rating = Number(avgRating);
  const band = rating >= 7 ? 'good' : rating >= 5 ? 'ok' : 'poor';
  return (
    <span className={`accom-rating ${band}`}>
      {rating}/10 <em>({stayCount} stay{stayCount === 1 ? '' : 's'})</em>
    </span>
  );
}

const EMPTY_FORM = {
  name: '',
  accommodation_type: 'hotel',
  address_line_1: '',
  address_line_2: '',
  town_city: '',
  county_region: '',
  postcode: '',
  country_code: 'GB',
  lat: '',
  lng: '',
  phone: '',
  booking_url: '',
  typical_price_per_night: '',
  notes: ''
};

export function AccommodationFormModal({ authFetch, editing, originSiteId, onClose, onSaved }) {
  const { showToast } = useToast();
  const [form, setForm] = useState(editing ? {
    ...EMPTY_FORM,
    ...Object.fromEntries(Object.keys(EMPTY_FORM).map(key => [key, editing[key] ?? ''])),
    country_code: editing.country_code || 'GB'
  } : EMPTY_FORM);
  const [finding, setFinding] = useState(false);
  const [findMessage, setFindMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const findCoordinates = async () => {
    setFinding(true);
    setFindMessage(null);
    try {
      const response = await authFetch('/api/v1/geocode-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address_line_1: form.address_line_1,
          address_line_2: form.address_line_2,
          town_city: form.town_city,
          county_region: form.county_region,
          postcode: form.postcode,
          country_code: form.country_code || 'GB'
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setForm(prev => ({ ...prev, lat: String(data.latitude), lng: String(data.longitude) }));
        setFindMessage({ type: 'ok', text: `Matched: ${data.displayName || 'address'}` });
      } else {
        setFindMessage({ type: 'error', text: data.error || 'No match found — check the address' });
      }
    } catch {
      setFindMessage({ type: 'error', text: 'Lookup failed — try again' });
    } finally {
      setFinding(false);
    }
  };

  /**
   * Pin the stay where the device is — useful standing in the car park, since a
   * hotel's postcode can land on the far side of a dual carriageway and the
   * nearby-stays lists are distance-ranked off these coordinates.
   *
   * accommodations has no geocode_* columns (lat/lng are NOT NULL and that is
   * the whole model), so unlike sites and depots this stores position only.
   */
  const handleDevicePin = (result) => {
    if (result.ok) {
      setForm(prev => ({ ...prev, lat: result.lat, lng: result.lng }));
    }
    // This panel's success class is 'ok'; 'warning' and 'error' match as-is.
    setFindMessage({ type: result.status === 'success' ? 'ok' : result.status, text: result.message });
  };

  const save = async (e) => {
    e.preventDefault();
    // Coordinates are now typeable, so they need the same guards the site form
    // has always had — nearby-stays lists are distance-ranked off these.
    const coordinateError = validateCoordinatePair(form.lat, form.lng, {
      required: true,
      noun: 'accommodation'
    });
    if (coordinateError) {
      const noneYet = !form.lat && !form.lng;
      setFindMessage({
        type: 'error',
        text: noneYet
          ? 'Set coordinates first — find them from the address, use your current location if you are there, or type them in. Nearby lists need a map position.'
          : coordinateError
      });
      return;
    }
    setSaving(true);
    try {
      const url = editing ? `/api/v1/accommodation/${editing.accommodation_id}` : '/api/v1/accommodation';
      const response = await authFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          typical_price_per_night: form.typical_price_per_night === '' ? null : Number(form.typical_price_per_night),
          origin_site_id: editing ? undefined : (originSiteId || undefined)
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to save');
      showToast(editing ? 'Accommodation updated' : 'Accommodation added', 'success');
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content accom-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editing ? 'Edit accommodation' : 'Add accommodation'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="accom-form-row">
              <div className="form-group grow">
                <label>Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={form.accommodation_type} onChange={e => set('accommodation_type', e.target.value)}>
                  {ACCOMMODATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Address line 1</label>
              <input value={form.address_line_1} onChange={e => set('address_line_1', e.target.value)} />
            </div>
            <div className="accom-form-row accom-address-row">
              <div className="form-group grow">
                <label>Town / city</label>
                <input value={form.town_city} onChange={e => set('town_city', e.target.value)} />
              </div>
              <div className="form-group accom-postcode">
                <label>Postcode</label>
                <input value={form.postcode} onChange={e => set('postcode', e.target.value)} />
              </div>
              <div className="form-group accom-country">
                <label>Country</label>
                <CountrySelect value={form.country_code} onChange={e => set('country_code', e.target.value)} />
              </div>
            </div>
            <div className="accom-form-row accom-coords-row">
              <button type="button" className="btn-secondary" onClick={findCoordinates} disabled={finding}>
                {finding ? 'Finding…' : 'Find coordinates'}
              </button>
              <UseMyLocationButton
                noun="stay"
                className="btn-secondary"
                disabled={finding}
                onResult={handleDevicePin}
              />
            </div>
            {/* Editable, like the site form: a geocode that lands on the wrong
                side of the road is easier to nudge than to re-find, and these
                coordinates rank the nearby-stays lists. */}
            <div className="accom-form-row accom-coords-row">
              <div className="form-group">
                <label htmlFor="accom_lat">Latitude</label>
                <input
                  id="accom_lat"
                  type="number"
                  step="0.0000001"
                  value={form.lat}
                  onChange={e => set('lat', e.target.value)}
                  placeholder="e.g. 56.1189342"
                />
              </div>
              <div className="form-group">
                <label htmlFor="accom_lng">Longitude</label>
                <input
                  id="accom_lng"
                  type="number"
                  step="0.0000001"
                  value={form.lng}
                  onChange={e => set('lng', e.target.value)}
                  placeholder="e.g. -3.9367215"
                />
              </div>
              <span className="accom-coords">
                {form.lat && form.lng ? 'Position set' : 'No coordinates yet'}
              </span>
            </div>
            {findMessage && (
              <div className={`accom-find-message ${findMessage.type}`}>{findMessage.text}</div>
            )}
            <div className="accom-form-row">
              <div className="form-group grow">
                <label>Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Typical £/night</label>
                <input type="number" min="0" step="1" value={form.typical_price_per_night} onChange={e => set('typical_price_per_night', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Booking link</label>
              <input value={form.booking_url} onChange={e => set('booking_url', e.target.value)} placeholder="https://…" />
            </div>
            <div className="form-group">
              <label>Notes for bookers</label>
              <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Van parking, late check-in, early breakfast…" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StayModal({ authFetch, accommodation, onClose, onSaved }) {
  const { showToast } = useToast();
  const [stayDate, setStayDate] = useState(() => toDateKey(new Date()));
  const [rating, setRating] = useState('');
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await authFetch(`/api/v1/accommodation/${accommodation.accommodation_id}/stays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stay_date: stayDate, rating: Number(rating), comments })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to log stay');
      showToast('Stay logged', 'success');
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content accom-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Log a stay — {accommodation.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            <div className="accom-form-row">
              <div className="form-group">
                <label>Stay date</label>
                <input type="date" value={stayDate} onChange={e => setStayDate(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Rating (1–10) *</label>
                <select value={rating} onChange={e => setRating(e.target.value)} required>
                  <option value="">Rate…</option>
                  {Array.from({ length: 10 }, (_, i) => 10 - i).map(n => (
                    <option key={n} value={n}>{n}{n === 10 ? ' — excellent' : n === 1 ? ' — terrible' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Comments</label>
              <textarea rows={3} value={comments} onChange={e => setComments(e.target.value)} placeholder="What should the next booker know?" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Log stay'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SuitabilityModal({ authFetch, accommodation, onClose, onSaved }) {
  const { showToast } = useToast();
  const reinstating = accommodation.is_suitable === false;
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await authFetch(`/api/v1/accommodation/${accommodation.accommodation_id}/suitability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_suitable: reinstating, reason: reinstating ? undefined : reason })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to update');
      showToast(reinstating ? 'Accommodation reinstated' : 'Marked not suitable', 'success');
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content accom-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{reinstating ? 'Reinstate' : 'Mark not suitable'} — {accommodation.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body">
            {reinstating ? (
              <p className="accom-hint">
                This place is currently marked not suitable{accommodation.unsuitable_reason ? ` (“${accommodation.unsuitable_reason}”)` : ''}.
                Reinstating makes it bookable again.
              </p>
            ) : (
              <div className="form-group">
                <label>Why is it not suitable? *</label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="This is the warning the next booker sees"
                  required
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className={reinstating ? 'btn-primary' : 'btn-danger'} disabled={saving}>
              {saving ? 'Saving…' : reinstating ? 'Reinstate' : 'Mark not suitable'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StayHistory({ authFetch, accommodationId }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/accommodation/${accommodationId}`);
        if (response.ok && !cancelled) setDetail((await response.json()).data);
      } catch (err) {
        console.error('Failed to fetch accommodation detail:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, accommodationId]);

  if (!detail) return <div className="accom-history">Loading…</div>;

  return (
    <div className="accom-history">
      {detail.notes && <p className="accom-history-notes">{detail.notes}</p>}
      {detail.booking_url && (
        <p><a href={detail.booking_url} target="_blank" rel="noopener noreferrer">Booking link</a></p>
      )}
      {detail.stays.length === 0 ? (
        <p className="accom-history-empty">No stays logged yet.</p>
      ) : (
        detail.stays.map(stay => (
          <div key={stay.stay_id} className="accom-stay-row">
            <span className={`accom-stay-rating ${stay.rating >= 7 ? 'good' : stay.rating >= 5 ? 'ok' : 'poor'}`}>{stay.rating}/10</span>
            <span className="accom-stay-meta">
              {formatDateKey(stay.stay_date)}
              {stay.engineer_name ? ` · ${stay.engineer_name}` : ''}
            </span>
            {stay.comments && <span className="accom-stay-comments">{stay.comments}</span>}
          </div>
        ))
      )}
    </div>
  );
}

function Accommodation() {
  const { authFetch, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canBook = BOOKING_ROLES.includes(user?.role);
  // Coming back to the directory (the browser's Back, or a reload) shows it
  // as it was left; a fresh visit starts from the address.
  const memory = useListMemory('/accommodation');

  const [rows, setRows] = useState([]);
  const [siteContext, setSiteContext] = useState(null);
  const [siteWarning, setSiteWarning] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(() => memory.initial('searchInput', ''));
  const [searchTerm, setSearchTerm] = useState(() => memory.initial('search', ''));
  const [hideUnsuitable, setHideUnsuitable] = useState(() => memory.initial('hideUnsuitable', false));
  const [expanded, setExpanded] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stayFor, setStayFor] = useState(null);
  const [suitabilityFor, setSuitabilityFor] = useState(null);

  // The "Near" site is in the address, so it is remembered with it.
  const siteId = searchParams.get('site_id') || '';

  useRememberList('/accommodation', { searchInput, search: searchTerm, hideUnsuitable });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (siteId) params.set('site_id', siteId);
      if (hideUnsuitable) params.set('suitable_only', 'true');
      const response = await authFetch(`/api/v1/accommodation?${params}`);
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setRows(data.data || []);
        setSiteContext(data.site || null);
        setSiteWarning(data.warning || null);
      }
    } catch (err) {
      console.error('Failed to fetch accommodation:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, searchTerm, siteId, hideUnsuitable]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    (async () => {
      try {
        const response = await authFetch('/api/v1/accommodation/site-options');
        if (response.ok) setSiteOptions((await response.json()).data || []);
      } catch (err) {
        console.error('Failed to fetch site options:', err);
      }
    })();
  }, [authFetch]);

  const setSiteFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('site_id', value); else next.delete('site_id');
    setSearchParams(next, { replace: true });
  };

  const closeAndRefresh = () => {
    setAdding(false);
    setEditing(null);
    setStayFor(null);
    setSuitabilityFor(null);
    fetchRows();
  };

  return (
    <div className="assets-page accom-page">
      <div className="page-header">
        <div>
          <h1>Accommodation</h1>
          <p>
            Vetted places to stay near sites. Rate stays out of 10 and mark bad ones not
            suitable so they never get booked again.
          </p>
        </div>
        {canBook && (
          <button className="btn-primary" onClick={() => setAdding(true)}>+ Add Accommodation</button>
        )}
      </div>

      <div className="assets-filters">
        <form onSubmit={e => { e.preventDefault(); setSearchTerm(searchInput); }} className="search-form">
          <input
            type="text"
            placeholder="Search name, town or postcode..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
        </form>
        <select value={siteId} onChange={e => setSiteFilter(e.target.value)} className="filter-select">
          <option value="">All locations</option>
          {siteOptions.map(site => (
            <option key={site.site_id} value={site.site_id}>Near: {site.customer_name} — {site.site_name}</option>
          ))}
        </select>
        <label className="accom-toggle">
          <input type="checkbox" checked={hideUnsuitable} onChange={e => setHideUnsuitable(e.target.checked)} />
          Hide not suitable
        </label>
      </div>

      {siteContext && (
        <p className="accom-context">
          Showing places within 25 miles of <strong>{siteContext.site_name}</strong>, nearest first.
        </p>
      )}
      {siteWarning && <div className="accom-warning">{siteWarning}</div>}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="accom-empty">
          {siteId ? 'Nothing logged near this site yet.' : 'No accommodation logged yet.'}
          {canBook ? ' Use + Add Accommodation to build the directory.' : ''}
        </div>
      ) : (
        <div className="accom-table-wrap">
          <table className="accom-table">
            <thead>
              <tr>
                <th>Place</th>
                {siteContext && <th>Distance</th>}
                <th>Rating</th>
                <th>£/night</th>
                <th>Phone</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <React.Fragment key={row.accommodation_id}>
                  <tr className={row.is_suitable === false ? 'unsuitable' : ''}>
                    <td>
                      <button type="button" className="accom-name" onClick={() => setExpanded(expanded === row.accommodation_id ? null : row.accommodation_id)}>
                        {row.name}
                      </button>
                      <span className="accom-sub">
                        {typeLabel(row.accommodation_type)}
                        {row.town_city ? ` · ${row.town_city}` : ''}
                        {row.postcode ? ` · ${row.postcode}` : ''}
                      </span>
                      <CoordsCopy lat={row.lat} lng={row.lng} compact />
                    </td>
                    {siteContext && <td>{row.distance_miles !== null ? `${row.distance_miles} mi` : '—'}</td>}
                    <td><RatingChip avgRating={row.avg_rating} stayCount={row.stay_count} /></td>
                    <td>{row.typical_price_per_night ? `£${Number(row.typical_price_per_night).toFixed(0)}` : '—'}</td>
                    <td>{row.phone ? <a href={`tel:${row.phone}`}>{row.phone}</a> : '—'}</td>
                    <td>
                      {row.is_suitable === false ? (
                        <span className="accom-status bad" title={row.unsuitable_reason || ''}>
                          Not suitable{row.unsuitable_reason ? ` — ${row.unsuitable_reason}` : ''}
                        </span>
                      ) : (
                        <span className="accom-status ok">Suitable</span>
                      )}
                    </td>
                    <td className="accom-actions">
                      {canBook && (
                        <>
                          <button className="btn-small" onClick={() => setStayFor(row)}>Log stay</button>
                          <button className="btn-small" onClick={() => setEditing(row)}>Edit</button>
                          <button className="btn-small" onClick={() => setSuitabilityFor(row)}>
                            {row.is_suitable === false ? 'Reinstate' : 'Not suitable'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {expanded === row.accommodation_id && (
                    <tr className="accom-expand-row">
                      <td colSpan={siteContext ? 7 : 6}>
                        <StayHistory authFetch={authFetch} accommodationId={row.accommodation_id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(adding || editing) && (
        <AccommodationFormModal
          authFetch={authFetch}
          editing={editing}
          originSiteId={siteId || null}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={closeAndRefresh}
        />
      )}
      {stayFor && (
        <StayModal authFetch={authFetch} accommodation={stayFor} onClose={() => setStayFor(null)} onSaved={closeAndRefresh} />
      )}
      {suitabilityFor && (
        <SuitabilityModal authFetch={authFetch} accommodation={suitabilityFor} onClose={() => setSuitabilityFor(null)} onSaved={closeAndRefresh} />
      )}
    </div>
  );
}

export default Accommodation;
